import { ErrorHandler, Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { UiRoutes } from '@lsq-frontend/angular-shared/app-constants';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { SETTINGS } from '@lsq-frontend/angular-shared/settings';
import {
    FastTrackRouteQueryParams,
    PlatformId
} from '@lsq-frontend/fasttrack-shared/app-constants';
import { AppSettings } from '@lsq-frontend/fasttrack-shared/environment';
import {
    ADMIN,
    AR_APPLICATION,
    atlas,
    ENROLLMENT,
    FACTORING,
    LOGIN,
    NOT_FOUND,
    SELECT_ACCOUNT,
    SUPPLY_CHAIN_DIRECT_ACCOUNT,
    SUPPLY_CHAIN_DIRECT_BUYER,
    SUPPLY_CHAIN_DIRECT_SELLER,
    SUPPLY_CHAIN_PLATFORM
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { GetAccountSummaryForUserService } from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import { DashboardClaim, User, UserStatus } from '@lsq-frontend/remote-services/enterprise/common';
import { getDynamicRouteLink, ignoreNullish } from '@lsq-frontend/common-ui';
import { ClaimService, getRouterLinkUrlById } from '@lsq-frontend/common-ui';
import { lowerFirst } from 'lodash';
import { first, shareReplay, switchMap } from 'rxjs/operators';
import { EnrollmentService } from '../enrollment/enrollment.service';

@Injectable({ providedIn: 'root' })
export class AppDashboardRoutingService {
    private _rootUrlsByClaim: Map<DashboardClaim, string>;
    private _enrollmentIsPreIdentityVerification = this._userService.user$.pipe(
        ignoreNullish(),
        switchMap(() => this._enrollmentService.enrollmentIsPreIdentityVerification()),
        shareReplay(1)
    );
    private _enrollmentIsIncomplete = this._userService.user$.pipe(
        ignoreNullish(),
        switchMap(() => this._enrollmentService.enrollmentIsIncomplete()),
        shareReplay(1)
    );

    constructor(
        private _userService: UserService,
        private _claimService: ClaimService,
        @Inject(SETTINGS) private _settings: AppSettings,
        private _enrollmentService: EnrollmentService,
        private _errorHandler: ErrorHandler,
        private _getAccountSummaryForUserService: GetAccountSummaryForUserService,
        private _router: Router
    ) {}

    private _populateRootUrls(): void {
        this._rootUrlsByClaim = new Map<DashboardClaim, string>([
            [DashboardClaim.FactoringDashboard, getRouterLinkUrlById(atlas, FACTORING)],
            [DashboardClaim.MobileInvoiceUpload, getRouterLinkUrlById(atlas, FACTORING)],
            [
                DashboardClaim.FinancingVendorPortal,
                getRouterLinkUrlById(atlas, SUPPLY_CHAIN_DIRECT_SELLER)
            ],
            [
                DashboardClaim.FinancingBuyerPortal,
                getRouterLinkUrlById(atlas, SUPPLY_CHAIN_DIRECT_BUYER)
            ],
            [
                DashboardClaim.FinancingAccountPortal,
                getRouterLinkUrlById(atlas, SUPPLY_CHAIN_DIRECT_ACCOUNT)
            ],
            [
                DashboardClaim.FinancingPlatformEnrollment,
                getDynamicRouteLink(getRouterLinkUrlById(atlas, SUPPLY_CHAIN_PLATFORM), {
                    platformId: PlatformId.DEFAULT
                })
            ],
            [DashboardClaim.ProspectApply, getRouterLinkUrlById(atlas, AR_APPLICATION)]
        ]);
    }

    public async getDashboardRootUrl(stateUrl: string = ''): Promise<string> {
        const user = await this._userService.user$.pipe(ignoreNullish(), first()).toPromise();

        // User is mid SCF Enrollment.
        // Redirect to Enrollment.
        if (
            (await this._enrollmentIsIncomplete.pipe(first()).toPromise()) ||
            (await this._enrollmentIsPreIdentityVerification.pipe(first()).toPromise())
        ) {
            return getRouterLinkUrlById(atlas, ENROLLMENT);
        }

        // User is required to set up MFA.
        // Redirect to Login MFA setup page.
        if (user.userStatus === UserStatus.MultiFactorAuthenticationSetupRequired) {
            return `${getRouterLinkUrlById(atlas, LOGIN)}?${
                FastTrackRouteQueryParams.MultiFactorAuthenticationSetup
            }=true`;
        }

        // User has no current company or has new connection request.
        // Redirect to Switch Account.
        if (await this._shouldGoToSwitchAccount(user)) {
            const showUpdateMfaModal = this._router.getCurrentNavigation().extractedUrl?.queryParams
                ?.showUpdateMfaModal;
            if (!showUpdateMfaModal) {
                return getRouterLinkUrlById(atlas, SELECT_ACCOUNT);
            } else {
                return `${getRouterLinkUrlById(atlas, SELECT_ACCOUNT)}?${
                    FastTrackRouteQueryParams.ShowUpdateMfaModal
                }=${showUpdateMfaModal}`;
            }
        }

        // User is Admin and not currently impersonating.
        // Redirect to Admin Overview.
        const appName = lowerFirst(this._settings.applicationName);
        const rawAppClaims = user.claims[appName];
        if (rawAppClaims?.includes(DashboardClaim.SystemManagement) && !user.isImpersonating) {
            return getRouterLinkUrlById(atlas, ADMIN);
        }

        // Decide where to redirect user based on claims.
        const claims = await this._claimService.claimStream.pipe(first()).toPromise();
        this._populateRootUrls();
        if (stateUrl.startsWith('/?')) {
            const queryParams = this._router.getCurrentNavigation().extractedUrl?.queryParams;
            for (const claim of Object.keys(claims)) {
                if (claims[claim] && this._rootUrlsByClaim.has(+claim)) {
                    if (!queryParams) {
                        return this._rootUrlsByClaim.get(+claim);
                    } else {
                        return `${this._rootUrlsByClaim.get(+claim)}${stateUrl.substring(1)}`;
                    }
                }
            }
        }

        for (const claim of Object.keys(claims)) {
            if (claims[claim] && this._rootUrlsByClaim.has(+claim)) {
                return this._rootUrlsByClaim.get(+claim);
            }
        }

        const claimNumbers = Object.keys(claims);
        if (
            !(
                claimNumbers.length === 1 &&
                claimNumbers.includes(DashboardClaim.Application.toString())
            )
        ) {
            this._errorHandler.handleError(
                new Error(
                    `${
                        user.emailAddress
                    } was unable to be routed to a homepage because they had claims ${Object.keys(
                        claims
                    ).join(' ')}`
                )
            );
        }

        return getRouterLinkUrlById(atlas, NOT_FOUND);
    }

    private async _shouldGoToSwitchAccount(user: User): Promise<boolean> {
        const hasMultipleCompanies = !user.currentCompany && !(user.companies.length === 1);
        const isComingFromLogin =
            this._router.url.startsWith?.(`/${UiRoutes.Login}`) || this._router.url === '';
        const hasNewNetworkRequestComingFromLogin =
            isComingFromLogin &&
            (await this._getAccountSummaryForUserService
                .executeOnce({ refUserKey: user?.userKey })
                .then((accountSummary) => accountSummary.networkRequestInformation.length > 0)
                .catch((e) => {
                    /*
                    This is specific for the demo env.
                    There is no java env for this call.
                    For the demo user we are returning false.
                    */
                    if (e.code == '404') return false;
                    else throw e;
                }));
        return hasMultipleCompanies || hasNewNetworkRequestComingFromLogin;
    }
}
